import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Camera, CheckCircle2, ImagePlus, Loader2, PaintBucket, Phone, Navigation } from "lucide-react";
import { telHref, mapsDirUrl } from "@/lib/maps";
import { toast } from "sonner";

function PaintPodDialog({ stop, onDone }) {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [receivedBy, setReceivedBy] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const camRef = useRef(null);
  const galRef = useRef(null);
  const addFiles = (e) => setFiles((f) => [...f, ...Array.from(e.target.files || [])].slice(0, 5));

  const submit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("photos", f));
      fd.append("received_by", receivedBy); fd.append("note", note);
      await api.post(`/paint/run/${stop.id}/delivered`, fd);
      toast.success(`Delivered — ${stop.customer.name}${stop.customer.phone ? " has been texted" : ""}`);
      setOpen(false); setFiles([]); setReceivedBy(""); setNote(""); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid={`paint-stop-done-${stop.id}`} className="gap-1.5 bg-kilted-green hover:bg-kilted-greenLight">
          <CheckCircle2 className="h-3.5 w-3.5" /> Delivered
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="paint-pod-dialog">
        <DialogHeader>
          <DialogTitle>Paint delivered — {stop.customer.name}</DialogTitle>
          <DialogDescription>Snap a photo where you left it and note who took it. The customer gets a delivery text if we have their mobile.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Proof photo(s)</Label>
            <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={addFiles} data-testid="paint-pod-camera-input" />
            <input ref={galRef} type="file" accept="image/*" multiple className="hidden" onChange={addFiles} data-testid="paint-pod-photo-input" />
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => camRef.current?.click()} data-testid="paint-pod-take-photo" className="flex-1 gap-2 border-kilted-green text-kilted-green hover:bg-kilted-green/10"><Camera className="h-4 w-4" /> Take photo</Button>
              <Button type="button" variant="outline" onClick={() => galRef.current?.click()} data-testid="paint-pod-upload-photo" className="flex-1 gap-2 border-kilted-green text-kilted-green hover:bg-kilted-green/10"><ImagePlus className="h-4 w-4" /> Upload</Button>
            </div>
            {files.length > 0 && <p className="text-xs text-emerald-700" data-testid="paint-pod-photo-count">{files.length} photo{files.length === 1 ? "" : "s"} ready</p>}
          </div>
          <div className="space-y-1.5"><Label>Received by</Label><Input value={receivedBy} onChange={(e) => setReceivedBy(e.target.value)} placeholder="Name, or 'left at roller door'" data-testid="paint-pod-received-by" /></div>
          <div className="space-y-1.5"><Label>Note</Label><Textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 300))} rows={2} data-testid="paint-pod-note" /></div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} data-testid="paint-pod-submit" className="gap-2 bg-kilted-green hover:bg-kilted-greenLight">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Confirm delivered
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PaintRunCard() {
  const [stops, setStops] = useState(null);
  const load = useCallback(() => api.get("/paint/run").then((r) => setStops(r.data.stops)).catch(() => setStops([])), []);
  useEffect(() => { load(); }, [load]);

  if (!stops?.length) return null;
  return (
    <Card className="mt-8 border-kilted-gold/60" data-testid="paint-run-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg text-kilted-green">
          <PaintBucket className="h-5 w-5 text-kilted-gold" /> Paint deliveries
          <span className="rounded-full bg-kilted-gold/20 px-2 py-0.5 text-xs font-bold">{stops.length}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {stops.map((o, i) => (
          <div key={o.id} className="flex flex-wrap items-start gap-3 rounded-md border bg-white p-3 text-sm" data-testid={`paint-stop-${o.id}`}>
            <div className="min-w-0 flex-1 space-y-0.5">
              <p className="font-bold">{o.run_sequence != null && <span className="mr-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-kilted-green text-[10px] text-white">{i + 1}</span>}{o.customer.name} <span className="text-xs font-normal text-muted-foreground">· from {o.shop_name} · {o.created_at.slice(0, 10)}</span></p>
              {o.customer.address && (
                <a href={mapsDirUrl({ line1: o.customer.address, lat: o.customer.lat, lng: o.customer.lng })} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-muted-foreground hover:text-kilted-green"><Navigation className="h-3 w-3" /> {o.customer.address}</a>
              )}
              {o.customer.phone && <a href={telHref(o.customer.phone)} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-kilted-green"><Phone className="h-3 w-3" /> {o.customer.phone}</a>}
              <p className="text-xs">{o.lines.map((l) => `${l.qty}× ${l.label}`).join(", ")}</p>
              {o.note && <p className="rounded bg-kilted-gold/15 px-2 py-1 text-xs font-medium text-kilted-green" data-testid={`paint-stop-note-${o.id}`}>Note: {o.note}</p>}
            </div>
            <PaintPodDialog stop={o} onDone={load} />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
